import React, {Suspense, useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {Canvas, useFrame} from "@react-three/fiber";
import { LightV1, fromRenderStructDTOArray, RenderLayout } from '@3dcalc/3dpClient'
import {fetchCalculation} from "../../api/calculations";

const DEMO_ID = process.env.REACT_APP_HERO_DEMO_ID || '787d8945-c592-4c1d-9727-7d6a8c8df603'

const AutoRotateScene = () => {
    const group = useRef();
    const [struct, setStruct] = useState([])
    const [palette] = useState([])

    useEffect(() => {
        if (!DEMO_ID) return
        fetchCalculation(DEMO_ID)
            .then(data => setStruct(fromRenderStructDTOArray(data.data)))
            .catch(() => setStruct([]))
    }, [])

    useFrame((_, delta) => {
        if (group.current) {
            group.current.rotation.y += delta * 0.4;
        }
    });

    return (
        <group ref={group} position={[0, -2, 0]}>
            <RenderLayout
                struct={struct}
                palette={palette}
                onPaletteChange={() => {}}
                onColorChange={() => {}}
            />
        </group>
    );
};

const ConfiguratorPreview = () => (
    <div className="relative w-full h-72 sm:h-80 lg:h-96 rounded-2xl overflow-hidden
                  bg-gray-950 border border-white/8">
        <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
                <span className="text-white/20 text-sm tracking-widest uppercase">Загрузка…</span>
            </div>
        }>
            <Canvas
                camera={{ position: [15, 0, 2], fov: 30 }}
                gl={{ antialias: true }}
            >
                <LightV1/>
                <AutoRotateScene />
            </Canvas>
        </Suspense>
    </div>
);

const HeroSection = () => {
    const { t } = useTranslation();

    const scrollTo = (e, href) => {
        e.preventDefault();
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <section className="relative min-h-screen bg-gray-950 flex items-center overflow-hidden">

            <div className="pointer-events-none absolute inset-0">
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-emerald-600/8
                        rounded-full blur-3xl -translate-x-1/2 -translate-y-1/3" />
                <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-emerald-500/6
                        rounded-full blur-2xl" />
            </div>

            <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

                    <div className="flex flex-col gap-7">

            <span className="inline-flex items-center gap-2 px-3 py-1.5 w-fit rounded-full
                             bg-emerald-500/10 border border-emerald-500/20">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-semibold tracking-widest uppercase">
                {t('hero.badge')}
              </span>
            </span>

                        <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-bold text-white
                           leading-[1.08] tracking-tight">
                            {t('hero.title')}
                        </h1>

                        <p className="text-lg text-gray-400 leading-relaxed max-w-md">
                            {t('hero.subtitle')}
                        </p>

                        <div className="flex flex-wrap gap-3">
                        <a
                            href="#demo"
                            onClick={(e) => scrollTo(e, '#demo')}
                            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold
                            rounded-xl text-sm transition-all duration-200
                            shadow-lg shadow-emerald-950/50 hover:-translate-y-0.5"
                            >
                            {t('hero.tryDemo')}
                        </a>

                        <a
                            href="#contact"
                            onClick={(e) => scrollTo(e, '#contact')}
                            className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold
                            rounded-xl text-sm border border-white/10 hover:border-white/20
                            transition-all duration-200 hover:-translate-y-0.5"
                        >
                            {t('hero.bookDemo')}
                        </a>
                </div>

                <p className="text-gray-600 text-sm">{t('hero.hint')}</p>
            </div>

            <div>
                <ConfiguratorPreview />
            </div>

        </div>
</div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10
                      flex flex-col items-center gap-1.5 pointer-events-none">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-white/15" />
        <div className="w-1 h-1 rounded-full bg-white/30" />
    </div>
</section>
);
};

export default HeroSection;
